import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, AlertDescription, Badge, Button, Card, EmptyState, Spinner } from '@agion/ui'
import { RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { PageHeader } from '@/layouts/InfraPageHeader'
import { listUpdates, applyUpdate } from './infra_update.service'

type UpdateSeverity = 'critical' | 'high' | 'medium' | 'low'

interface ComponentUpdate {
  id: string
  component: string
  currentVersion: string
  availableVersion: string
  severity: UpdateSeverity
  environment: string
}

function severityBadgeVariant(severity: UpdateSeverity) {
  switch (severity) {
    case 'critical':
      return 'error' as const
    case 'high':
      return 'warning' as const
    case 'medium':
      return 'info' as const
    case 'low':
      return 'default' as const
  }
}

export function UpdatesPage() {
  const { t } = useTranslation(['pages'])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [updates, setUpdates] = useState<ComponentUpdate[]>([])
  const [applyingId, setApplyingId] = useState<string | null>(null)

  const loadUpdates = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await listUpdates()
      const mapped: ComponentUpdate[] = response.data.map(u => ({
        id: u.id,
        component: u.name,
        currentVersion: u.currentVersion,
        availableVersion: u.version,
        severity: u.severity,
        environment: u.componentType,
      }))
      setUpdates(mapped)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load updates')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadUpdates()
  }, [loadUpdates])

  async function handleApplyUpdate(updateId: string) {
    try {
      setApplyingId(updateId)
      await applyUpdate(updateId)
      setUpdates((prev) => prev.filter((u) => u.id !== updateId))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply update')
    } finally {
      setApplyingId(null)
    }
  }

  const hasCritical = updates.some((u) => u.severity === 'critical')

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        icon={RefreshCw}
        title={t('pages:updates.title', 'Updates')}
        description={t('pages:updates.description', 'Available updates for environments and components')}
      />

      {hasCritical && (
        <Alert variant="error">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {t(
              'pages:updates.criticalAlert',
              'Critical updates are available. Apply them as soon as possible to maintain security.'
            )}
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <Card>
          <div className="p-8 text-center text-red-600">{error}</div>
        </Card>
      ) : updates.length === 0 ? (
        <EmptyState
          icon={CheckCircle2}
          title={t('pages:updates.empty', 'All up to date')}
          description={t('pages:updates.emptyDesc', 'All components are running the latest versions.')}
        />
      ) : (
        <Card>
          <div className="grid grid-cols-6 gap-4 px-4 py-3 bg-slate-50 dark:bg-slate-800/50 text-xs font-medium text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
            <span>{t('pages:updates.columns.component', 'Component')}</span>
            <span>{t('pages:updates.columns.current', 'Current Version')}</span>
            <span>{t('pages:updates.columns.available', 'Available Version')}</span>
            <span>{t('pages:updates.columns.severity', 'Severity')}</span>
            <span>{t('pages:updates.columns.environment', 'Environment')}</span>
            <span>{t('pages:updates.columns.actions', 'Actions')}</span>
          </div>
          <div className="divide-y divide-slate-200 dark:divide-slate-700">
            {updates.map((update) => (
              <div
                key={update.id}
                className="grid grid-cols-6 gap-4 px-4 py-3 text-sm items-center hover:bg-slate-50 dark:hover:bg-slate-800/50"
              >
                <span className="text-slate-900 dark:text-slate-100 font-medium">
                  {update.component}
                </span>
                <span className="text-slate-500 dark:text-slate-400 font-mono text-xs">
                  {update.currentVersion}
                </span>
                <span className="text-slate-900 dark:text-slate-100 font-mono text-xs">
                  {update.availableVersion}
                </span>
                <span>
                  <Badge variant={severityBadgeVariant(update.severity)}>
                    {update.severity}
                  </Badge>
                </span>
                <span className="text-slate-600 dark:text-slate-400">{update.environment}</span>
                <span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleApplyUpdate(update.id)}
                    disabled={applyingId === update.id}
                  >
                    <RefreshCw className={`h-3 w-3 mr-1 ${applyingId === update.id ? 'animate-spin' : ''}`} />
                    {applyingId === update.id
                      ? t('pages:updates.applying', 'Applying...')
                      : t('pages:updates.apply', 'Apply Update')}
                  </Button>
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}
